import React, { useState, useEffect } from 'react';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { getCurrentUserCountry, setUserCountry, detectUserCountry } from '../../util/countryDetection';
import { supportedCountries, getCountryPricing } from '../../config/configMultiCurrency';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import css from './CountrySelector.module.css';

const { Money } = sdkTypes;

const CountrySelector = props => {
  const { className, rootClassName, onCountryChange, showPricing = false } = props;
  const intl = useIntl();
  const [currentCountry, setCurrentCountry] = useState(null);
  const [isDetecting, setIsDetecting] = useState(false);

  useEffect(() => {
    // Get current country on component mount
    const country = getCurrentUserCountry();
    setCurrentCountry(country);
  }, []);

  const handleCountryChange = (event) => {
    const newCountryCode = event.target.value;
    try {
      const newCountry = setUserCountry(newCountryCode);
      setCurrentCountry(newCountry);
      
      if (onCountryChange) {
        onCountryChange(newCountry);
      }
    } catch (error) {
      console.error('Error setting country:', error);
    }
  };

  const handleAutoDetect = async () => {
    setIsDetecting(true);
    try {
      const detectedCountry = await detectUserCountry();
      setCurrentCountry(detectedCountry);
      
      if (onCountryChange) {
        onCountryChange(detectedCountry);
      }
    } catch (error) {
      console.error('Error detecting country:', error);
    } finally {
      setIsDetecting(false);
    }
  };

  if (!currentCountry) {
    return <div className={css.loading}>Loading...</div>;
  }

  const countryOptions = supportedCountries.map(country => ({
    key: country.code,
    label: `${country.name} (${country.currency})`
  }));

  const currentPricing = getCountryPricing(currentCountry.countryCode);

  return (
    <div className={rootClassName || css.root}>
      <div className={css.header}>
        <h3 className={css.title}>
          <FormattedMessage id="CountrySelector.title" defaultMessage="Select your country" />
        </h3>
        <button 
          type="button" 
          onClick={handleAutoDetect} 
          disabled={isDetecting}
          className={css.autoDetectButton}
        >
          {isDetecting ? (
            <FormattedMessage id="CountrySelector.detecting" defaultMessage="Detecting..." />
          ) : (
            <FormattedMessage id="CountrySelector.autoDetect" defaultMessage="Auto-detect" />
          )}
        </button>
      </div>

      <div className={css.selectWrapper}>
        <label htmlFor="countrySelector" className={css.selectLabel}>
          {intl.formatMessage({ id: 'CountrySelector.countryLabel', defaultMessage: 'Country' })}
        </label>
        <select
          id="countrySelector"
          name="country"
          className={`${css.select} ${className || ''}`}
          value={currentCountry.countryCode}
          onChange={handleCountryChange}
        >
          {countryOptions.map(option => (
            <option key={option.key} value={option.key}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {showPricing && (
        <div className={css.pricingInfo}>
          <h4 className={css.pricingTitle}>
            <FormattedMessage 
              id="CountrySelector.pricingTitle" 
              defaultMessage="Base pricing for {country}"
              values={{ country: supportedCountries.find(c => c.code === currentCountry.countryCode)?.name }}
            />
          </h4>
          <div className={css.pricingGrid}>
            <div className={css.pricingItem}>
              <span className={css.pricingLabel}>
                <FormattedMessage id="CountrySelector.hourlyRate" defaultMessage="Hourly" />
              </span>
              <span className={css.pricingValue}>
                {formatMoney(intl, new Money(currentPricing.basePricing.hourly, currentCountry.currency))}
              </span>
            </div>
            <div className={css.pricingItem}>
              <span className={css.pricingLabel}>
                <FormattedMessage id="CountrySelector.dailyRate" defaultMessage="Daily" />
              </span>
              <span className={css.pricingValue}>
                {formatMoney(intl, new Money(currentPricing.basePricing.daily, currentCountry.currency))}
              </span>
            </div>
            <div className={css.pricingItem}>
              <span className={css.pricingLabel}>
                <FormattedMessage id="CountrySelector.fixedRate" defaultMessage="Fixed" />
              </span>
              <span className={css.pricingValue}>
                {formatMoney(intl, new Money(currentPricing.basePricing.fixed, currentCountry.currency))}
              </span>
            </div>
          </div>
          <div className={css.serviceFeeInfo}>
            <FormattedMessage 
              id="CountrySelector.serviceFeeInfo" 
              defaultMessage="Service fee: {percentage}%"
              values={{ percentage: currentPricing.defaultServiceFee }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CountrySelector; 